import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  Crown,
  Loader2,
  Search
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Signal Form Component - defined outside to prevent re-renders
const SignalForm = ({ formData, setFormData, onSubmit, onCancel, submitting, submitText }) => {
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Symbol *</Label>
          <Input
            placeholder="BTC/USDT"
            value={formData.symbol}
            onChange={(e) => handleChange('symbol', e.target.value.toUpperCase())}
            className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
            data-testid="signal-symbol-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Bias / Direction *</Label>
          <Select value={formData.bias} onValueChange={(value) => handleChange('bias', value)}>
            <SelectTrigger className="bg-black border-[#1F1F1F] text-white" data-testid="signal-bias-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#1F1F1F]">
              <SelectItem value="LONG" className="text-[#00FF94] focus:bg-[#00FF94]/10 focus:text-[#00FF94]">LONG / Bullish</SelectItem>
              <SelectItem value="SHORT" className="text-[#FF0055] focus:bg-[#FF0055]/10 focus:text-[#FF0055]">SHORT / Bearish</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#EDEDED]">Title</Label>
        <Input
          placeholder="Signal title or description"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
          data-testid="signal-title-input"
        />
      </div>

      {/* Entry Zone */}
      <div className="border border-[#1F1F1F] rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-[#00FF94]">Entry Zone</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">Entry Min</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.entry_zone_min}
              onChange={(e) => handleChange('entry_zone_min', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-entry-min-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">Entry Max</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.entry_zone_max}
              onChange={(e) => handleChange('entry_zone_max', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-entry-max-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Entry Notes</Label>
          <Input
            placeholder="Entry conditions or notes"
            value={formData.entry_notes}
            onChange={(e) => handleChange('entry_notes', e.target.value)}
            className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
            data-testid="signal-entry-notes-input"
          />
        </div>
      </div>

      {/* Stop Loss */}
      <div className="border border-[#1F1F1F] rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-[#FF0055]">Stop Loss</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">Defensive SL</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.defensive_sl}
              onChange={(e) => handleChange('defensive_sl', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-defensive-sl-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">Aggressive SL</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.aggressive_sl}
              onChange={(e) => handleChange('aggressive_sl', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-aggressive-sl-input"
            />
          </div>
        </div>
      </div>

      {/* Take Profits */}
      <div className="border border-[#1F1F1F] rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-[#00FF94]">Take Profit Targets</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">TP 1</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.tp1}
              onChange={(e) => handleChange('tp1', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-tp1-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">TP 2</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.tp2}
              onChange={(e) => handleChange('tp2', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-tp2-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#EDEDED]">TP 3</Label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={formData.tp3}
              onChange={(e) => handleChange('tp3', e.target.value)}
              className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
              data-testid="signal-tp3-input"
            />
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Risk/Reward</Label>
          <Input
            placeholder="2.5"
            value={formData.risk_reward}
            onChange={(e) => handleChange('risk_reward', e.target.value)}
            className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
            data-testid="signal-rr-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Leverage</Label>
          <Input
            type="number"
            step="any"
            placeholder="10"
            value={formData.leverage_recommended}
            onChange={(e) => handleChange('leverage_recommended', e.target.value)}
            className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
            data-testid="signal-leverage-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#EDEDED]">Volatility</Label>
          <Select value={formData.volatility || ''} onValueChange={(value) => handleChange('volatility', value)}>
            <SelectTrigger className="bg-black border-[#1F1F1F] text-white" data-testid="signal-volatility-select">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-[#1F1F1F]">
              <SelectItem value="Low" className="text-white focus:bg-white/5 focus:text-white">Low</SelectItem>
              <SelectItem value="Medium" className="text-white focus:bg-white/5 focus:text-white">Medium</SelectItem>
              <SelectItem value="High" className="text-white focus:bg-white/5 focus:text-white">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Premium Toggle */}
      <div className="flex items-center justify-between p-4 border border-[#1F1F1F] rounded-lg">
        <div>
          <Label className="text-[#EDEDED]">Premium Signal</Label>
          <p className="text-xs text-[#737373]">Only premium users can view this signal</p>
        </div>
        <Switch
          checked={formData.Premium}
          onCheckedChange={(checked) => handleChange('Premium', checked)}
          className="data-[state=checked]:bg-[#6366F1]"
          data-testid="signal-premium-switch"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label className="text-[#EDEDED]">Notes</Label>
        <Textarea
          placeholder="Add analysis notes..."
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          className="bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373] min-h-[100px]"
          data-testid="signal-notes-input"
        />
      </div>

      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-[#1F1F1F] bg-transparent hover:bg-white/5 text-white"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold"
          data-testid="signal-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            submitText
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state - matching actual API schema
  const [formData, setFormData] = useState({
    symbol: '',
    title: '',
    bias: 'LONG',
    entry_zone_min: '',
    entry_zone_max: '',
    entry_notes: '',
    defensive_sl: '',
    aggressive_sl: '',
    tp1: '',
    tp2: '',
    tp3: '',
    risk_reward: '',
    leverage_recommended: '',
    volatility: '',
    notes: '',
    Premium: false
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/signals');
      return;
    }
    fetchSignals();
  }, [isAdmin, navigate, token]);

  const fetchSignals = async () => {
    try {
      const response = await axios.get(`${API}/trading_setups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch signals:', error);
      toast.error('Failed to load signals');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = useCallback(() => {
    setFormData({
      symbol: '',
      title: '',
      bias: 'LONG',
      entry_zone_min: '',
      entry_zone_max: '',
      entry_notes: '',
      defensive_sl: '',
      aggressive_sl: '',
      tp1: '',
      tp2: '',
      tp3: '',
      risk_reward: '',
      leverage_recommended: '',
      volatility: '',
      notes: '',
      Premium: false
    });
  }, []);

  const buildPayload = useCallback(() => {
    const payload = {
      symbol: formData.symbol,
    };

    // Only add non-empty values
    if (formData.title) payload.title = formData.title;
    if (formData.bias) payload.bias = formData.bias;
    if (formData.entry_zone_min) payload.entry_zone_min = parseFloat(formData.entry_zone_min);
    if (formData.entry_zone_max) payload.entry_zone_max = parseFloat(formData.entry_zone_max);
    if (formData.entry_notes) payload.entry_notes = formData.entry_notes;
    if (formData.defensive_sl) payload.defensive_sl = parseFloat(formData.defensive_sl);
    if (formData.aggressive_sl) payload.aggressive_sl = parseFloat(formData.aggressive_sl);
    if (formData.tp1) payload.tp1 = parseFloat(formData.tp1);
    if (formData.tp2) payload.tp2 = parseFloat(formData.tp2);
    if (formData.tp3) payload.tp3 = parseFloat(formData.tp3);
    if (formData.risk_reward) payload.risk_reward = formData.risk_reward;
    if (formData.leverage_recommended) payload.leverage_recommended = parseFloat(formData.leverage_recommended);
    if (formData.volatility) payload.volatility = formData.volatility;
    if (formData.notes) payload.notes = formData.notes;
    payload.Premium = formData.Premium;

    return payload;
  }, [formData]);

  const handleCreateSignal = async () => {
    if (!formData.symbol) {
      toast.error('Symbol is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      await axios.post(`${API}/trading_setups`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Signal created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchSignals();
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to create signal';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSignal = async () => {
    if (!formData.symbol) {
      toast.error('Symbol is required');
      return;
    }

    setSubmitting(true);
    try {
      const signalId = selectedSignal._id || selectedSignal.id;
      const payload = buildPayload();

      await axios.patch(`${API}/trading_setups/${signalId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Signal updated successfully');
      setShowEditModal(false);
      setSelectedSignal(null);
      resetForm();
      fetchSignals();
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to update signal';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSignal = async () => {
    setSubmitting(true);
    try {
      const signalId = selectedSignal._id || selectedSignal.id;
      await axios.delete(`${API}/trading_setups/${signalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Signal deleted successfully');
      setShowDeleteModal(false);
      setSelectedSignal(null);
      fetchSignals();
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to delete signal';
      toast.error(typeof message === 'object' ? JSON.stringify(message) : message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (signal) => {
    setSelectedSignal(signal);
    setFormData({
      symbol: signal.symbol || '',
      title: signal.title || '',
      bias: signal.bias || 'LONG',
      entry_zone_min: signal.entry_zone_min?.toString() || '',
      entry_zone_max: signal.entry_zone_max?.toString() || '',
      entry_notes: signal.entry_notes || '',
      defensive_sl: signal.defensive_sl?.toString() || '',
      aggressive_sl: signal.aggressive_sl?.toString() || '',
      tp1: signal.tp1?.toString() || '',
      tp2: signal.tp2?.toString() || '',
      tp3: signal.tp3?.toString() || '',
      risk_reward: signal.risk_reward?.toString() || '',
      leverage_recommended: signal.leverage_recommended?.toString() || '',
      volatility: signal.volatility || '',
      notes: signal.notes || '',
      Premium: Boolean(signal.Premium)
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (signal) => {
    setSelectedSignal(signal);
    setShowDeleteModal(true);
  };

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    resetForm();
  }, [resetForm]);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    resetForm();
  }, [resetForm]);

  const filteredSignals = signals.filter(signal => 
    signal.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    signal.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper to determine direction from bias field
  const isLongSignal = (signal) => {
    const bias = signal.bias?.toLowerCase() || '';
    return bias.includes('long') || bias.includes('bullish') || bias.includes('buy');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Manage trading signals
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold"
            data-testid="create-signal-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Signal
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <Input
            placeholder="Search by symbol or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black border-[#1F1F1F] focus:border-[#00FF94] text-white placeholder:text-[#737373]"
            data-testid="admin-search-input"
          />
        </div>

        {/* Signals Table */}
        <Card className="border-[#1F1F1F] bg-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="text-lg text-white">All Signals ({filteredSignals.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1F1F1F] hover:bg-transparent">
                    <TableHead className="text-[#737373]">Symbol</TableHead>
                    <TableHead className="text-[#737373]">Bias</TableHead>
                    <TableHead className="text-[#737373]">Entry</TableHead>
                    <TableHead className="text-[#737373]">SL</TableHead>
                    <TableHead className="text-[#737373]">TP1</TableHead>
                    <TableHead className="text-[#737373]">R:R</TableHead>
                    <TableHead className="text-[#737373]">Type</TableHead>
                    <TableHead className="text-[#737373] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSignals.length === 0 ? (
                    <TableRow className="border-[#1F1F1F]">
                      <TableCell colSpan={8} className="text-center py-8 text-[#737373]">
                        No signals found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSignals.map((signal) => {
                      const isLong = isLongSignal(signal);
                      const signalIsPremium = Boolean(signal.Premium);
                      const signalId = signal._id || signal.id;
                      
                      return (
                        <TableRow 
                          key={signalId} 
                          className="border-[#1F1F1F] hover:bg-white/5"
                          data-testid={`admin-signal-row-${signalId}`}
                        >
                          <TableCell className="font-mono text-white font-medium">
                            <div className="flex items-center gap-2">
                              {isLong ? (
                                <TrendingUp className="w-4 h-4 text-[#00FF94]" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-[#FF0055]" />
                              )}
                              <div>
                                <div>{signal.symbol}</div>
                                {signal.title && <div className="text-xs text-[#737373]">{signal.title}</div>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`font-mono text-xs uppercase ${
                              isLong ? 'text-[#00FF94]' : 'text-[#FF0055]'
                            }`}>
                              {signal.bias || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-white">
                            {signal.entry_zone_min || signal.entry_zone_max || '-'}
                          </TableCell>
                          <TableCell className="font-mono text-[#FF0055]">
                            {signal.defensive_sl || signal.aggressive_sl || '-'}
                          </TableCell>
                          <TableCell className="font-mono text-[#00FF94]">{signal.tp1 || '-'}</TableCell>
                          <TableCell className="font-mono text-white">{signal.risk_reward || '-'}</TableCell>
                          <TableCell>
                            {signalIsPremium ? (
                              <Badge className="bg-[#6366F1]/10 text-[#6366F1] border border-[#6366F1]/20">
                                <Crown className="w-3 h-3 mr-1" />
                                Premium
                              </Badge>
                            ) : (
                              <Badge className="bg-white/5 text-white/60 border border-white/10">
                                Free
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openEditModal(signal)}
                                className="h-8 w-8 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                                data-testid={`edit-signal-${signalId}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openDeleteModal(signal)}
                                className="h-8 w-8 text-[#A1A1AA] hover:text-[#FF0055] hover:bg-[#FF0055]/10"
                                data-testid={`delete-signal-${signalId}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Signal</DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Add a new trading signal for your users
              </DialogDescription>
            </DialogHeader>
            <SignalForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleCreateSignal}
              onCancel={handleCloseCreateModal}
              submitting={submitting}
              submitText="Create Signal"
            />
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Signal</DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Update the trading signal details
              </DialogDescription>
            </DialogHeader>
            <SignalForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleEditSignal}
              onCancel={handleCloseEditModal}
              submitting={submitting}
              submitText="Save Changes"
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Signal</DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Are you sure you want to delete the signal for <strong className="text-white">{selectedSignal?.symbol}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="border-[#1F1F1F] bg-transparent hover:bg-white/5 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteSignal}
                disabled={submitting}
                className="bg-[#FF0055] text-white hover:bg-[#FF0055]/90"
                data-testid="confirm-delete-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Signal'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Admin;
